import { useCallback, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Typography,
} from '@mui/material';

import { useUploadBillingMigrationFilesMutation } from '../store/billing-migration-api';
import {
  BILLING_MIGRATION_SOURCE_SYSTEMS,
  type SourceSystemValue,
} from '../utils/billing-migration-status-helpers';

interface BillingMigrationUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ACCEPTED_EXTENSIONS = ['.txt', '.edi', '.x12', '.zip'];

const isValidFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext));
};

export default function BillingMigrationUploadModal({
  open,
  onClose,
  onSuccess,
}: BillingMigrationUploadModalProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [sourceSystem, setSourceSystem] = useState<SourceSystemValue | ''>('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [uploadFiles, { isLoading }] = useUploadBillingMigrationFilesMutation();

  const handleClose = useCallback(() => {
    if (isLoading) return;
    setSelectedFiles([]);
    setSourceSystem('');
    setUploadProgress(0);
    setError(null);
    onClose();
  }, [isLoading, onClose]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const newFiles = Array.from(files).filter(isValidFile);
    if (newFiles.length === 0) {
      setError(`Invalid file type. Accepted: ${ACCEPTED_EXTENSIONS.join(', ')}`);
      return;
    }
    setError(null);
    setSelectedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        addFiles(e.target.files);
      }
      e.target.value = '';
    },
    [addFiles],
  );

  const handleRemoveFile = useCallback((index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSourceSystemChange = useCallback((e: SelectChangeEvent) => {
    setSourceSystem(e.target.value as SourceSystemValue);
    setError(null);
  }, []);

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file.');
      return;
    }
    if (!sourceSystem) {
      setError('Please select a source system.');
      return;
    }

    setError(null);
    setUploadProgress(25);

    try {
      await uploadFiles({
        files: selectedFiles,
        sourceSystem,
      }).unwrap();

      setUploadProgress(100);
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 500);
    } catch (err: unknown) {
      setUploadProgress(0);
      const apiError = err as { data?: { message?: string; error?: string; detail?: string }; message?: string };
      const message =
        apiError?.data?.message ||
        apiError?.data?.error ||
        apiError?.data?.detail ||
        apiError?.message ||
        'Upload failed. Please try again.';
      setError(message);
    }
  };

  const canUpload = selectedFiles.length > 0 && sourceSystem !== '' && !isLoading;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Upload Billing Files
        <IconButton onClick={handleClose} disabled={isLoading} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Upload .txt or .zip files containing 837 (claims) or 835 (ERA) data.
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="source-system-label">Source System</InputLabel>
          <Select
            labelId="source-system-label"
            value={sourceSystem}
            label="Source System"
            onChange={handleSourceSystemChange}
            disabled={isLoading}
          >
            {BILLING_MIGRATION_SOURCE_SYSTEMS.map(system => (
              <MenuItem key={system.value} value={system.value}>
                {system.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 3,
            textAlign: 'center',
            backgroundColor: isDragging ? 'action.hover' : 'background.default',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            mb: 2,
          }}
          onClick={() => document.getElementById('billing-file-input')?.click()}
        >
          <input
            id="billing-file-input"
            type="file"
            multiple
            accept={ACCEPTED_EXTENSIONS.join(',')}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="body1">Drag & drop files here, or click to select</Typography>
          <Typography variant="caption" color="text.secondary">
            Accepted: {ACCEPTED_EXTENSIONS.join(', ')} files
          </Typography>
        </Box>

        {selectedFiles.length > 0 && (
          <List dense>
            {selectedFiles.map((file, index) => (
              <ListItem key={`${file.name}-${index}`}>
                <ListItemIcon>
                  <InsertDriveFileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(1)} KB`}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" size="small" onClick={() => handleRemoveFile(index)} disabled={isLoading}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}

        {uploadProgress > 0 && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {uploadProgress < 100 ? 'Uploading...' : 'Upload complete'}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!canUpload}
        >
          {isLoading ? 'Uploading...' : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
